import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Globe, Plus, ArrowUp, ArrowDown, Eye, EyeOff, Trash2, Edit2,
  ExternalLink, Save, Check, Pin, Share2
} from 'lucide-react';
import { api, type PortalWidgetData } from '../lib/api';
import { R, ft, shadow, label, btnAccent, btnOutline, btnDangerSolid } from '../lib/ui';
import { docIcon } from '../lib/doc-icons';
import { Spinner } from '../components/Spinner';
import { useDialog } from '../components/DialogHost';

export function PortalAdmin() {
  const queryClient = useQueryClient();
  const dialog = useDialog();

  const { data, isLoading } = useQuery({
    queryKey: ['portal-admin'],
    queryFn: () => api.portalAdminGet(),
  });

  // Состояние общего конфига
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [badge, setBadge] = useState('');
  const [configSaved, setConfigSaved] = useState(false);

  // Состояние модального окна добавления/редактирования
  const [modalOpen, setModalOpen] = useState(false);
  const [editingWidget, setEditingWidget] = useState<PortalWidgetData | null>(null);

  // Форма виджета
  const [formDocId, setFormDocId] = useState('');
  const [formTableId, setFormTableId] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formCat, setFormCat] = useState('Общее');
  const [formIcon, setFormIcon] = useState('sheet');
  const [formPinned, setFormPinned] = useState(false);
  const [formEnabled, setFormEnabled] = useState(true);
  const [formPreview, setFormPreview] = useState(true);

  // Инициализация полей конфига при загрузке данных
  const initConfig = () => {
    if (data?.config) {
      setTitle(data.config.title || '');
      setSubtitle(data.config.subtitle || '');
      setBadge(data.config.badge || '');
    }
  };

  // Мутации
  const saveConfigMutation = useMutation({
    mutationFn: (cfg: { title: string; subtitle: string; badge: string }) =>
      api.portalUpdateConfig(cfg),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-admin'] });
      queryClient.invalidateQueries({ queryKey: ['portal-public'] });
      setConfigSaved(true);
      setTimeout(() => setConfigSaved(false), 2000);
    },
  });

  const saveWidgetMutation = useMutation({
    mutationFn: (payload: {
      id?: string;
      docId: string;
      tableId?: string;
      title: string;
      description?: string;
      category?: string;
      icon?: string;
      isPinned?: boolean;
      enabled?: boolean;
      previewRows?: boolean;
    }) => {
      if (payload.id) {
        return api.portalUpdateWidget(payload.id, payload);
      }
      return api.portalCreateWidget(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-admin'] });
      queryClient.invalidateQueries({ queryKey: ['portal-public'] });
      setModalOpen(false);
      setEditingWidget(null);
    },
  });

  const deleteWidgetMutation = useMutation({
    mutationFn: (id: string) => api.portalDeleteWidget(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-admin'] });
      queryClient.invalidateQueries({ queryKey: ['portal-public'] });
    },
  });

  const toggleEnabledMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.portalUpdateWidget(id, { enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-admin'] });
      queryClient.invalidateQueries({ queryKey: ['portal-public'] });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => api.portalReorderWidgets(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-admin'] });
      queryClient.invalidateQueries({ queryKey: ['portal-public'] });
    },
  });

  const openAddModal = () => {
    setEditingWidget(null);
    const firstDoc = data?.availableDocs[0];
    setFormDocId(firstDoc?.id || '');
    setFormTableId(firstDoc?.tables[0]?.id || '');
    setFormTitle(firstDoc?.name || '');
    setFormDesc('');
    setFormCat('Госслужба');
    setFormIcon(firstDoc?.icon || 'sheet');
    setFormPinned(false);
    setFormEnabled(true);
    setFormPreview(true);
    setModalOpen(true);
  };

  const openEditModal = (widget: PortalWidgetData) => {
    setEditingWidget(widget);
    setFormDocId(widget.docId);
    setFormTableId(widget.tableId || '');
    setFormTitle(widget.title);
    setFormDesc(widget.description || '');
    setFormCat(widget.category || 'Общее');
    setFormIcon(widget.icon || 'sheet');
    setFormPinned(widget.isPinned);
    setFormEnabled(widget.enabled);
    setFormPreview(widget.previewRows);
    setModalOpen(true);
  };

  const handleMove = (index: number, direction: 'up' | 'down') => {
    if (!data?.widgets) return;
    const items = [...data.widgets];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= items.length) return;
    const [moved] = items.splice(index, 1);
    items.splice(targetIndex, 0, moved);
    reorderMutation.mutate(items.map((w) => w.id));
  };

  const selectedDoc = data?.availableDocs.find((d) => d.id === formDocId);

  if (isLoading) {
    return (
      <div style={{ flex: 1, display: 'grid', placeItems: 'center', background: R.bg }}>
        <Spinner size={32} />
      </div>
    );
  }

  return (
    <div
      style={{
        flex: 1,
        overflowY: 'auto',
        background: R.bg,
        color: R.text,
        padding: '32px 36px',
        maxWidth: 1100,
        margin: '0 auto',
        width: '100%',
      }}
    >
      {/* ШАПКА АДМИНКИ */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 16,
          marginBottom: 28,
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img
              src="/favicon.png"
              alt="Redwood Tables"
              width={32}
              height={32}
              style={{
                width: 32,
                height: 32,
                borderRadius: 6,
                objectFit: 'contain',
                display: 'block',
              }}
            />
            <h1 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', margin: 0 }}>
              Управление государственными реестрами
            </h1>
          </div>
          <p style={{ color: R.textMuted, fontSize: 13, marginTop: 4 }}>
            Настройка публичного портала открытых данных и прикрепление виджетов таблиц для граждан
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <a
            href="/portal"
            target="_blank"
            rel="noreferrer"
            style={{
              ...btnOutline,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              textDecoration: 'none',
            }}
          >
            <ExternalLink size={14} />
            <span>Открыть портал</span>
          </a>

          <button
            onClick={openAddModal}
            style={{
              ...btnAccent,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Plus size={16} />
            <span>Добавить реестр</span>
          </button>
        </div>
      </div>

      {/* НАСТРОЙКИ ПОРТАЛА */}
      <div
        style={{
          background: R.bgSubtle,
          border: ft.edge,
          borderRadius: 8,
          padding: '20px 24px',
          marginBottom: 28,
          boxShadow: shadow.panel,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>
            Основные параметры публичного портала
          </h2>
          <button
            onClick={() => {
              saveConfigMutation.mutate({
                title: title || data?.config.title || 'Государственные реестры Redwood',
                subtitle: subtitle || data?.config.subtitle || '',
                badge: badge || data?.config.badge || '',
              });
            }}
            disabled={saveConfigMutation.isPending}
            style={{
              ...btnAccent,
              height: 30,
              padding: '0 12px',
              fontSize: 12,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            {configSaved ? <Check size={14} /> : <Save size={14} />}
            <span>{configSaved ? 'Сохранено' : 'Сохранить'}</span>
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 180px', gap: 14 }}>
          <div>
            <label style={{ ...label, display: 'block', marginBottom: 6 }}>Заголовок страницы</label>
            <input
              type="text"
              defaultValue={data?.config.title || ''}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Государственные реестры Redwood"
              style={{
                width: '100%',
                height: 34,
                padding: '0 10px',
                fontSize: 13,
                border: ft.edge,
                borderRadius: 4,
                background: R.bgInput,
                color: R.text,
              }}
            />
          </div>

          <div>
            <label style={{ ...label, display: 'block', marginBottom: 6 }}>Подзаголовок / Описание</label>
            <input
              type="text"
              defaultValue={data?.config.subtitle || ''}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder="Официальный публичный портал открытых данных"
              style={{
                width: '100%',
                height: 34,
                padding: '0 10px',
                fontSize: 13,
                border: ft.edge,
                borderRadius: 4,
                background: R.bgInput,
                color: R.text,
              }}
            />
          </div>

          <div>
            <label style={{ ...label, display: 'block', marginBottom: 6 }}>Текст плашки (badge)</label>
            <input
              type="text"
              defaultValue={data?.config.badge || ''}
              onChange={(e) => setBadge(e.target.value)}
              placeholder="Официальные реестры"
              style={{
                width: '100%',
                height: 34,
                padding: '0 10px',
                fontSize: 13,
                border: ft.edge,
                borderRadius: 4,
                background: R.bgInput,
                color: R.text,
              }}
            />
          </div>
        </div>
      </div>

      {/* ТАБЛИЦА ВИДЖЕТОВ */}
      <div
        style={{
          background: R.bgSubtle,
          border: ft.edge,
          borderRadius: 8,
          overflow: 'hidden',
          boxShadow: shadow.panel,
        }}
      >
        <div
          style={{
            padding: '16px 20px',
            borderBottom: ft.edge,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: R.bgElevated,
          }}
        >
          <div style={{ fontWeight: 800, fontSize: 14 }}>
            Прикреплённые виджеты таблиц ({data?.widgets.length ?? 0})
          </div>
          <div style={{ fontSize: 12, color: R.textMuted }}>
            Используйте стрелки для смены порядка отображения
          </div>
        </div>

        {data?.widgets.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: R.textMuted }}>
            Пока нет ни одного прикреплённого реестра. Нажмите «Добавить реестр», чтобы создать первый виджет.
          </div>
        ) : (
          <div>
            {data?.widgets.map((widget, index) => (
              <div
                key={widget.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '14px 20px',
                  borderBottom: ft.hair,
                  gap: 16,
                  opacity: widget.enabled ? 1 : 0.6,
                  transition: 'background 0.12s',
                }}
              >
                {/* СОРТИРОВКА */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <button
                    onClick={() => handleMove(index, 'up')}
                    disabled={index === 0}
                    style={{
                      padding: 2,
                      background: 'none',
                      border: 'none',
                      cursor: index === 0 ? 'default' : 'pointer',
                      color: index === 0 ? R.border : R.text,
                    }}
                  >
                    <ArrowUp size={14} />
                  </button>
                  <button
                    onClick={() => handleMove(index, 'down')}
                    disabled={index === (data.widgets.length - 1)}
                    style={{
                      padding: 2,
                      background: 'none',
                      border: 'none',
                      cursor: index === (data.widgets.length - 1) ? 'default' : 'pointer',
                      color: index === (data.widgets.length - 1) ? R.border : R.text,
                    }}
                  >
                    <ArrowDown size={14} />
                  </button>
                </div>

                {/* ИКОНКА */}
                {(() => {
                  const Icon = docIcon(widget.icon || 'sheet');
                  return (
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 6,
                        background: R.bgInput,
                        border: ft.hair,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: R.accent,
                        flexShrink: 0,
                      }}
                    >
                      <Icon size={18} />
                    </div>
                  );
                })()}

                {/* ИНФОРМАЦИЯ */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: R.text }}>
                      {widget.title}
                    </span>

                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 10,
                        background: R.accentSubtle,
                        color: R.accentText,
                        fontSize: 10.5,
                        fontWeight: 700,
                      }}
                    >
                      {widget.category}
                    </span>

                    {widget.isPinned && (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 3,
                          padding: '2px 8px',
                          borderRadius: 10,
                          background: 'rgba(217, 160, 102, 0.15)',
                          color: R.warning,
                          fontSize: 10.5,
                          fontWeight: 700,
                        }}
                      >
                        <Pin size={10} />
                        Закреплён
                      </span>
                    )}

                    {!widget.enabled && (
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: 10,
                          background: R.border,
                          color: R.textMuted,
                          fontSize: 10.5,
                          fontWeight: 700,
                        }}
                      >
                        Скрыт
                      </span>
                    )}
                  </div>

                  {widget.description && (
                    <div
                      style={{
                        fontSize: 12,
                        color: R.textMuted,
                        marginTop: 2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {widget.description}
                    </div>
                  )}
                </div>

                {/* ДЕЙСТВИЯ */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <button
                    onClick={() =>
                      toggleEnabledMutation.mutate({
                        id: widget.id,
                        enabled: !widget.enabled,
                      })
                    }
                    title={widget.enabled ? 'Скрыть с портала' : 'Опубликовать на портале'}
                    style={{
                      height: 30,
                      padding: '0 10px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 12,
                      borderRadius: 4,
                      border: ft.edge,
                      background: R.bgInput,
                      color: widget.enabled ? R.text : R.textMuted,
                      cursor: 'pointer',
                    }}
                  >
                    {widget.enabled ? <Eye size={13} /> : <EyeOff size={13} />}
                    <span>{widget.enabled ? 'Виден' : 'Скрыт'}</span>
                  </button>

                  <a
                    href={`/shared/${widget.shareToken}`}
                    target="_blank"
                    rel="noreferrer"
                    title="Открыть таблицу как гражданин"
                    style={{
                      height: 30,
                      padding: '0 10px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 12,
                      borderRadius: 4,
                      border: ft.edge,
                      background: R.bgInput,
                      color: R.text,
                      textDecoration: 'none',
                    }}
                  >
                    <Share2 size={13} />
                    <span>Ссылка</span>
                  </a>

                  <button
                    onClick={() => openEditModal(widget)}
                    title="Редактировать виджет"
                    style={{
                      height: 30,
                      width: 30,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 4,
                      border: ft.edge,
                      background: R.bgInput,
                      color: R.text,
                      cursor: 'pointer',
                    }}
                  >
                    <Edit2 size={13} />
                  </button>

                  <button
                    onClick={async () => {
                      const ok = await dialog.confirm({
                        title: 'Удалить виджет?',
                        body: `Удалить виджет «${widget.title}» с портала?`,
                        danger: true,
                        confirmLabel: 'Удалить',
                        cancelLabel: 'Отмена',
                      });
                      if (ok) {
                        deleteWidgetMutation.mutate(widget.id);
                      }
                    }}
                    title="Удалить виджет"
                    style={{
                      height: 30,
                      width: 30,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 4,
                      border: ft.edge,
                      background: R.dangerSubtle,
                      color: R.danger,
                      cursor: 'pointer',
                    }}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* МОДАЛЬНОЕ ОКНО ДОБАВЛЕНИЯ / РЕДАКТИРОВАНИЯ */}
      {modalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 60,
            background: 'rgba(0, 0, 0, 0.65)',
            display: 'grid',
            placeItems: 'center',
            padding: 20,
          }}
        >
          <div
            style={{
              width: 'min(640px, 100%)',
              background: R.bgSubtle,
              border: ft.strong,
              borderRadius: 8,
              padding: 24,
              boxShadow: shadow.dropdown,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ fontSize: 17, fontWeight: 800, margin: 0 }}>
                {editingWidget ? 'Редактировать виджет реестра' : 'Добавить реестр на публичный портал'}
              </h3>
              <button
                onClick={() => setModalOpen(false)}
                style={{ background: 'none', border: 'none', color: R.textMuted, cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            {/* ВЫБОР ДОКУМЕНТА И ТАБЛИЦЫ (только при создании) */}
            {!editingWidget && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ ...label, display: 'block', marginBottom: 4 }}>
                    Исходный документ
                  </label>
                  <select
                    value={formDocId}
                    onChange={(e) => {
                      const docId = e.target.value;
                      setFormDocId(docId);
                      const d = data?.availableDocs.find((x) => x.id === docId);
                      if (d) {
                        setFormTableId(d.tables[0]?.id || '');
                        setFormTitle(d.name);
                        setFormIcon(d.icon || 'sheet');
                      }
                    }}
                    style={{
                      width: '100%',
                      height: 34,
                      padding: '0 8px',
                      fontSize: 13,
                      border: ft.edge,
                      borderRadius: 4,
                      background: R.bgInput,
                      color: R.text,
                    }}
                  >
                    {data?.availableDocs.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ ...label, display: 'block', marginBottom: 4 }}>
                    Таблица / Лист
                  </label>
                  <select
                    value={formTableId}
                    onChange={(e) => setFormTableId(e.target.value)}
                    style={{
                      width: '100%',
                      height: 34,
                      padding: '0 8px',
                      fontSize: 13,
                      border: ft.edge,
                      borderRadius: 4,
                      background: R.bgInput,
                      color: R.text,
                    }}
                  >
                    {selectedDoc?.tables.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div>
              <label style={{ ...label, display: 'block', marginBottom: 4 }}>
                Отображаемое название реестра
              </label>
              <input
                type="text"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="Например: Единый реестр транспортных средств"
                style={{
                  width: '100%',
                  height: 34,
                  padding: '0 10px',
                  fontSize: 13,
                  border: ft.edge,
                  borderRadius: 4,
                  background: R.bgInput,
                  color: R.text,
                }}
              />
            </div>

            <div>
              <label style={{ ...label, display: 'block', marginBottom: 4 }}>
                Описание / Назначение реестра
              </label>
              <textarea
                value={formDesc}
                onChange={(e) => setFormDesc(e.target.value)}
                placeholder="Краткое описание для граждан: что содержит данный реестр и кем ведётся..."
                rows={2}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  fontSize: 13,
                  border: ft.edge,
                  borderRadius: 4,
                  background: R.bgInput,
                  color: R.text,
                  resize: 'vertical',
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ ...label, display: 'block', marginBottom: 4 }}>
                  Категория
                </label>
                <input
                  type="text"
                  value={formCat}
                  onChange={(e) => setFormCat(e.target.value)}
                  placeholder="Транспорт, Бизнес, Лицензии, Суд..."
                  style={{
                    width: '100%',
                    height: 34,
                    padding: '0 10px',
                    fontSize: 13,
                    border: ft.edge,
                    borderRadius: 4,
                    background: R.bgInput,
                    color: R.text,
                  }}
                />
              </div>

              <div>
                <label style={{ ...label, display: 'block', marginBottom: 4 }}>
                  Ключ иконки
                </label>
                <input
                  type="text"
                  value={formIcon}
                  onChange={(e) => setFormIcon(e.target.value)}
                  placeholder="truck, landmark, shield, sheet..."
                  style={{
                    width: '100%',
                    height: 34,
                    padding: '0 10px',
                    fontSize: 13,
                    border: ft.edge,
                    borderRadius: 4,
                    background: R.bgInput,
                    color: R.text,
                  }}
                />
              </div>
            </div>

            {/* ЧЕКБОКСЫ */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '4px 0' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formPreview}
                  onChange={(e) => setFormPreview(e.target.checked)}
                />
                <span>Показывать компактный мини-предпросмотр строк прямо в виджете</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formPinned}
                  onChange={(e) => setFormPinned(e.target.checked)}
                />
                <span>Закрепить виджет в начале списка портала (Pinned)</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formEnabled}
                  onChange={(e) => setFormEnabled(e.target.checked)}
                />
                <span>Опубликован на портале (включён)</span>
              </label>
            </div>

            {/* КНОПКИ ДЕЙСТВИЯ */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                style={btnOutline}
              >
                Отмена
              </button>

              <button
                type="button"
                onClick={async () => {
                  if (!formTitle.trim()) {
                    await dialog.alert({
                      title: 'Не заполнено обязательное поле',
                      body: 'Пожалуйста, укажите название реестра.',
                    });
                    return;
                  }
                  saveWidgetMutation.mutate({
                    id: editingWidget?.id,
                    docId: formDocId,
                    tableId: formTableId || undefined,
                    title: formTitle,
                    description: formDesc,
                    category: formCat,
                    icon: formIcon,
                    isPinned: formPinned,
                    enabled: formEnabled,
                    previewRows: formPreview,
                  });
                }}
                disabled={saveWidgetMutation.isPending}
                style={btnAccent}
              >
                {saveWidgetMutation.isPending ? 'Сохранение...' : editingWidget ? 'Сохранить изменения' : 'Добавить виджет'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
